import { useState } from "react";

import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { Check, Edit, FileText, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

export interface CallScriptListProps {
  callScripts: CallScript[];
  isLoading: boolean;
  isError: boolean;
  onEdit: (id: string) => void;
  onUpdateTitle: (script: CallScript) => void;
}

export function CallScriptList({
  callScripts,
  isLoading,
  isError,
  onEdit,
  onUpdateTitle,
}: Readonly<CallScriptListProps>) {
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState("");

  const handleTitleClick = (script: CallScript) => {
    setEditingTitleId(script.id);
    setEditedTitle(script.title);
  };

  const handleTitleSave = (script: CallScript) => {
    onUpdateTitle({ ...script, title: editedTitle });
    setEditingTitleId(null);
  };

  const handleTitleCancel = () => {
    setEditingTitleId(null);
  };

  return (
    <div className="flex h-full flex-col">
      <Card className="flex-grow overflow-hidden">
        <ScrollArea className="h-full">
          <div>
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : isError ? (
              <div className="flex h-full items-center justify-center text-red-500">
                Error loading call scripts. Please try again.
              </div>
            ) : callScripts.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                <div className="py-8">Create your first call script</div>
              </div>
            ) : (
              <Table>
                <TableBody>
                  {callScripts.map((script) => (
                    <TableRow key={script.id}>
                      <TableCell className="w-1/2">
                        <div className="flex items-center">
                          <FileText className="mr-2 h-4 w-4 flex-shrink-0" />
                          {editingTitleId === script.id ? (
                            <div className="flex flex-grow items-center">
                              <Input
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleTitleSave(script);
                                  }
                                }}
                                className="mr-2 flex-grow"
                              />
                              <div className="flex-shrink-0">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleTitleSave(script)}
                                  className="mr-1"
                                >
                                  <Check size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={handleTitleCancel}
                                >
                                  <X size={16} />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="flex-grow cursor-pointer hover:underline"
                              onClick={() => handleTitleClick(script)}
                            >
                              {script.title}
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="w-1/3 text-right text-xs text-gray-500">
                        Created: {new Date(script.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="w-1/6 text-right">
                        <Button
                          variant="secondary"
                          onClick={() => onEdit(script.id)}
                        >
                          <Edit size={16} className="mr-2" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
}
